/**
 * Nosto-PLP.js
 *
 * Inicializa y conecta todos los módulos utilizados por una página
 * de colección controlada mediante Nosto.
 *
 * Este archivo crea las instancias específicas del PLP. No contiene
 * lógica interna de búsquedas, filtros, cards ni vistas de producto.
 */

(function initializeNostoPlp(global) {
    "use strict";

    const SELECTORS = {
        root:
            "[data-nosto-plp]",

        results:
            "[data-nosto-plp-results]",

        filters:
            "[data-nosto-plp-filters]",

        sort:
            "[data-nosto-plp-sort]",

        loadMore:
            "[data-nosto-plp-load-more]",

        pagination:
            "[data-nosto-plp-pagination]",

        applyFilters:
            "[data-nosto-plp-apply-filters]",

        clearFilters:
            "[data-nosto-plp-clear-filters]",

        viewModeButtons:
            "[data-nosto-view-mode]",
    };

    const VIEW_MODES = [
        "normal",
        "editorial",
    ];

    const VIEW_MODE_STORAGE_KEY =
        "nosto-plp-view-mode";

    /**
     * Rollback rápido de la paginación híbrida.
     *
     * false: scroll infinito puro (NostoPlpController).
     * true: scroll infinito + paginación al completar cada página
     *       lógica (NostoHybridPlpController + NostoHybridPaginationManager).
     */
    const ENABLE_HYBRID_PAGINATION = false;

    const PRODUCTS_PER_LOGICAL_PAGE = 96;
    const LOGICAL_PAGE_PARAM = "nostoPage";

    /**
     * Obtiene la vista guardada durante la sesión actual.
     */
    function getStoredViewMode() {
        try {
            const storedMode =
                global.sessionStorage
                    ?.getItem(
                        VIEW_MODE_STORAGE_KEY
                    );

            return VIEW_MODES.includes(
                String(
                    storedMode || ""
                )
                    .trim()
                    .toLowerCase()
            )
                ? String(storedMode)
                    .trim()
                    .toLowerCase()
                : "";
        } catch (error) {
            return "";
        }
    }

    /**
     * Guarda la vista elegida durante la sesión actual.
     */
    function storeViewMode(
        viewMode
    ) {
        try {
            global.sessionStorage
                ?.setItem(
                    VIEW_MODE_STORAGE_KEY,
                    normalizeViewMode(
                        viewMode
                    )
                );

            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Devuelve un modo de vista permitido.
     */
    function normalizeViewMode(
        viewMode
    ) {
        const normalizedMode =
            String(
                viewMode || ""
            )
                .trim()
                .toLowerCase();

        return VIEW_MODES.includes(
            normalizedMode
        )
            ? normalizedMode
            : "normal";
    }

    /**
     * Comprueba que todas las clases necesarias estén disponibles.
     */
    function validateDependencies() {
        const requiredDependencies = [
            "NostoClient",
            "NostoFacetManager",
            "NostoHitStore",
            "NostoProductResultsRenderer",
            "NostoFacetRenderer",
            "NostoProductViewService",
            "NostoQuickBuyModal",
            "NostoPlpAdvertisementManager",
            "NostoPlpController",
            "NostoHybridPlpController",
            "NostoHybridPaginationManager",
        ];

        const missingDependencies =
            requiredDependencies.filter(
                (dependency) =>
                    typeof global[dependency] !==
                    "function"
            );

        if (missingDependencies.length) {
            throw new Error(
                [
                    "No fue posible inicializar el PLP de Nosto.",
                    `Faltan: ${missingDependencies.join(", ")}.`,
                ].join(" ")
            );
        }
    }

    /**
     * Obtiene la configuración Liquid asociada al PLP.
     */
    function getPageConfig(root) {
        return {
            categoryId:
                root.dataset.nostoCategoryId ||
                global.NOSTO_PAGE_CONTEXT
                    ?.categoryId ||
                "",

            categoryPath:
                root.dataset.nostoCategoryPath ||
                global.NOSTO_PAGE_CONTEXT
                    ?.categoryPath ||
                "",

            pageSize:
                Number(
                    root.dataset.nostoPageSize ||
                    global.NOSTO_PAGE_CONTEXT
                        ?.pageSize ||
                    12
                ),

            initialViewMode:
                normalizeViewMode(
                    root.dataset.nostoViewMode ||
                    root.dataset.nostoCardType ||
                    global.NOSTO_PAGE_CONTEXT
                        ?.viewMode ||
                    global.NOSTO_PAGE_CONTEXT
                        ?.cardType ||
                    "normal"
                ),

            debug:
                root.dataset.nostoDebug ===
                "true" ||
                global.NOSTO_DEBUG === true,
        };
    }

    /**
     * Crea el cliente compartido utilizado para comunicarse con Nosto.
     */
    function createClient(config) {
        return new global.NostoClient({
            debug: config.debug,
        });
    }

    /**
     * Crea el manager de filtros específico del PLP.
     */
    function createFacetManager(config) {
        const facetManager =
            new global.NostoFacetManager({
                definitions:
                    global.NOSTO_FACET_DEFINITIONS ||
                    [],

                colorGroups:
                    global.NOSTO_COLOR_GROUPS ||
                    {},

                colorFields: [
                    "customFields.custom-color",
                ],

                priceField:
                    "price",

                syncUrl: true,
                urlMode: "replace",

                debug:
                    config.debug,
            });

        if (
            facetManager.hasManagedUrlState()
        ) {
            facetManager.restoreStateFromUrl(
                global.location.href,
                {
                    emit: false,
                    syncUrl: false,
                }
            );
        }

        return facetManager;
    }

    /**
     * Crea el servicio encargado de solicitar las vistas Liquid.
     */
    function createProductViewService(
        config
    ) {
        return new global
            .NostoProductViewService({
                cardView:
                    "product-card",

                quickBuyView:
                    "nosto-quick-buy",

                concurrency: 4,
                cache: true,

                debug:
                    config.debug,
            });
    }

    /**
 * Crea o reutiliza el modal global de compra rápida.
 */
    function createQuickBuyModal({
        productViewService,
        config,
    }) {
        if (
            global.NOSTO_QUICK_BUY_MODAL
        ) {
            return global
                .NOSTO_QUICK_BUY_MODAL;
        }

        const quickBuyModal =
            new global.NostoQuickBuyModal({
                viewService:
                    productViewService,

                debug:
                    config.debug,
            });

        quickBuyModal.init();

        global.NOSTO_QUICK_BUY_MODAL =
            quickBuyModal;

        return quickBuyModal;
    }

    /**
     * Crea el renderer de filtros específico del PLP.
     */
    function createFacetRenderer({
        facetManager,
        config,
    }) {
        return new global
            .NostoFacetRenderer({
                container:
                    SELECTORS.filters,

                facetManager,

                labels:
                    global.NOSTO_FACET_LABELS ||
                    {},

                hiddenFields: [
                    "customFields.shasa-color",
                ],

                colorField:
                    "customFields.custom-color",

                priceField:
                    "price",

                sizeField:
                    "skus.customFields.talla",

                sizeOrder:
                    global.NOSTO_SIZE_ORDER ||
                    [],

                fieldOrder: [
                    "skus.customFields.talla",
                    "customFields.custom-color",
                    "customFields.custom-fits",
                    "customFields.custom-product_group_code",
                    "customFields.custom-pg_filtro",
                    "customFields.custom-material",
                    "price",
                ],

                applyButton:
                    SELECTORS.applyFilters,

                clearButton:
                    SELECTORS.clearFilters,

                autoApply: false,
                defaultOpen: true,

                currency:
                    global.Shopify?.currency
                        ?.active ||
                    "MXN",

                currencySymbol: "$",

                showCounts: true,
                showPriceLabels: false,

                debug:
                    config.debug,
            });
    }

    /**
     * Crea el renderer de resultados usando exclusivamente el ProductCard universal.
     *
     * El modo normal/editorial sigue perteneciendo al layout del PLP completo.
     * Ya no cambia la vista Liquid del card ni crea controladores legacy de card.
     */
    function createResultsRenderer({
        productViewService,
        advertisementManager,
        getController,
        getViewMode,
        config,
    }) {
        const normalizeString = (value) =>
            String(value ?? "").trim();

        const normalizeProductId = (value) => {
            const normalized =
                normalizeString(value);

            if (!normalized) {
                return "";
            }

            return normalized
                .split("/")
                .filter(Boolean)
                .at(-1);
        };

        const getHandle = (hit) => {
            const directHandle =
                normalizeString(
                    hit?.handle ||
                    hit?.productHandle
                );

            if (directHandle) {
                return directHandle;
            }

            const url =
                normalizeString(
                    hit?.url ||
                    hit?.productUrl
                );

            if (!url) {
                return "";
            }

            try {
                const parsed =
                    new URL(
                        url,
                        global.location.origin
                    );

                const parts =
                    parsed.pathname
                        .split("/")
                        .filter(Boolean);

                const index =
                    parts.indexOf(
                        "products"
                    );

                return index >= 0
                    ? parts[index + 1] || ""
                    : "";
            } catch (error) {
                return "";
            }
        };

        return new global
            .NostoProductResultsRenderer({
                container:
                    SELECTORS.results,

                placeholderTagName: "li",
                statusTagName: "li",

                placeholderClass:
                    "nosto-product-placeholder",

                productItemClass:
                    "grid-item",

                emptyMessage:
                    "No encontramos productos con los filtros seleccionados.",

                errorMessage:
                    "No fue posible cargar los productos. Intenta nuevamente.",

                onRenderComplete() {
                    advertisementManager
                        ?.sync(
                            getViewMode()
                        );

                    const results =
                        document.querySelector(
                            SELECTORS.results
                        );

                    syncEditorialProductCards(
                        results,
                        getViewMode()
                    );
                },

                debug:
                    config.debug,

                productCardFactory:
                    async ({
                        hit,
                        productId,
                        container:
                        productContainer,
                        context:
                        rendererContext = {},
                    }) => {
                        const normalizedProductId =
                            normalizeProductId(
                                productId
                            );

                        const handle =
                            getHandle(hit);

                        if (!handle) {
                            throw new Error(
                                `No fue posible obtener el handle para el producto ${normalizedProductId || "desconocido"}.`
                            );
                        }

                        const html =
                            await productViewService
                                .getView({
                                    handle,

                                    productId:
                                        normalizedProductId,

                                    view:
                                        "product-card",

                                    validationSelector:
                                        "[data-product-card]",

                                    context: {
                                        ...rendererContext,

                                        source:
                                            "nosto-category",

                                        track:
                                            "category",

                                        viewMode:
                                            getViewMode(),
                                    },
                                });

                        const template =
                            document.createElement(
                                "template"
                            );

                        template.innerHTML =
                            String(html).trim();

                        const sourceCard =
                            template.content
                                .querySelector(
                                    "[data-product-card]"
                                );

                        if (!sourceCard) {
                            throw new Error(
                                "La vista product-card no contiene [data-product-card]."
                            );
                        }

                        const card =
                            sourceCard.cloneNode(
                                true
                            );

                        const EAGER_CARDS = 6;
                        const HIGH_PRIORITY_CARDS = 2;

                        const gridPosition =
                            Array.prototype.indexOf.call(
                                productContainer.parentElement?.children || [],
                                productContainer
                            );

                        card
                            .querySelectorAll("img.product-card__image")
                            .forEach((img) => {
                                const isInitialImage = Boolean(
                                    img.closest(
                                        ".product-card__media-group.is-active [data-product-card-initial]"
                                    )
                                );

                                if (
                                    gridPosition >= 0 &&
                                    gridPosition < EAGER_CARDS &&
                                    isInitialImage
                                ) {
                                    img.loading = "eager";
                                    img.setAttribute(
                                        "fetchpriority",
                                        gridPosition < HIGH_PRIORITY_CARDS
                                            ? "high"
                                            : "auto"
                                    );
                                } else {
                                    img.loading = "lazy";
                                    img.setAttribute("fetchpriority", "low");
                                }
                            });

                        card.dataset.productSource =
                            "nosto-category";

                        card.dataset.nostoTrack =
                            "category";

                        card.dataset
                            .nostoOriginalProductId =
                            normalizedProductId;

                        productContainer
                            .replaceChildren(
                                card
                            );

                        global.initializeProductCards?.(
                            productContainer
                        );

                        const handleCardClick =
                            (event) => {
                                const link =
                                    event.target.closest(
                                        "[data-product-card-link]"
                                    );

                                if (
                                    !link ||
                                    !card.contains(link)
                                ) {
                                    return;
                                }

                                void getController()
                                    ?.recordProductClick(
                                        normalizedProductId,
                                        {
                                            fallbackHit:
                                                hit,
                                        }
                                    );
                            };

                        card.addEventListener(
                            "click",
                            handleCardClick
                        );

                        return {
                            root:
                                card,

                            destroy() {
                                card.removeEventListener(
                                    "click",
                                    handleCardClick
                                );

                                card
                                    .__productCardInstance
                                    ?.destroy?.();
                            },
                        };
                    },
            });
    }

    /**
     * Aplica el estado editorial directamente sobre cada ProductCard.
     *
     * Esto evita depender únicamente de un selector del section root y
     * también cubre cards agregados posteriormente por infinite scroll.
     */
    function syncEditorialProductCards(
        root,
        viewMode
    ) {
        const isEditorial =
            normalizeViewMode(
                viewMode
            ) === "editorial";

        root
            ?.querySelectorAll(
                "[data-product-card]"
            )
            .forEach((card) => {
                card.classList.toggle(
                    "is-editorial-product-card",
                    isEditorial
                );
            });
    }

    /**
     * Sincroniza el estado visual de los botones de vista.
     */
    function syncViewModeButtons({
        root,
        viewMode,
    }) {
        root
            .querySelectorAll(
                SELECTORS.viewModeButtons
            )
            .forEach(
                (button) => {
                    const buttonMode =
                        normalizeViewMode(
                            button.dataset
                                .nostoViewMode
                        );

                    const isActive =
                        buttonMode ===
                        viewMode;

                    button.classList.toggle(
                        "is-active",
                        isActive
                    );

                    button.setAttribute(
                        "aria-pressed",
                        String(isActive)
                    );
                }
            );
    }

    /**
     * Conecta los botones que cambian la vista del PLP.
     */
    function bindViewModeControls({
        root,
        getViewMode,
        setViewMode,
    }) {
        const buttons =
            Array.from(
                root.querySelectorAll(
                    SELECTORS.viewModeButtons
                )
            );

        if (!buttons.length) {
            return;
        }

        buttons.forEach(
            (button) => {
                button.addEventListener(
                    "click",
                    () => {
                        const nextMode =
                            normalizeViewMode(
                                button.dataset
                                    .nostoViewMode
                            );

                        if (
                            nextMode ===
                            getViewMode()
                        ) {
                            return;
                        }

                        setViewMode(
                            nextMode,
                            {
                                persist: true,
                                emit: true,
                            }
                        );
                    }
                );
            }
        );

        syncViewModeButtons({
            root,
            viewMode:
                getViewMode(),
        });
    }

    /**
 * Conecta la X y el overlay con el control original del drawer.
 */
    function bindFiltersDrawerClose() {
        const drawer =
            document.querySelector(
                "[data-collection-sidebar]"
            );

        const trigger =
            document.querySelector(
                '[aria-controls^="filter-groups-"]'
            );

        if (
            !drawer ||
            !trigger
        ) {
            console.warn(
                "[Nosto-Plp] No se encontraron los controles del drawer de filtros."
            );

            return;
        }

        document.addEventListener(
            "click",
            (event) => {
                const closeControl =
                    event.target.closest(
                        "[data-collection-sidebar-close]"
                    );

                if (!closeControl) {
                    return;
                }

                event.preventDefault();

                const isExpanded =
                    trigger.getAttribute(
                        "aria-expanded"
                    ) === "true";

                if (isExpanded) {
                    trigger.click();
                }
            }
        );
    }


    /**
     * Inicializa todos los componentes del PLP.
     */
    async function init() {
        const root =
            document.querySelector(
                SELECTORS.root
            );

        if (!root) {
            return null;
        }

        if (
            root.dataset.nostoInitialized ===
            "true" ||
            root.dataset.nostoInitializing ===
            "true"
        ) {
            return (
                global.NOSTO_PLP ||
                null
            );
        }

        root.dataset.nostoInitializing =
            "true";

        validateDependencies();

        const config =
            getPageConfig(root);

        let currentViewMode =
            getStoredViewMode() ||
            config.initialViewMode;

        root.dataset.nostoViewMode =
            currentViewMode;

        const client =
            createClient(config);

        const facetManager =
            createFacetManager(config);

        const hitStore =
            new global.NostoHitStore({
                debug:
                    config.debug,
            });

        const productViewService =
            createProductViewService(
                config
            );

        const quickBuyModal =
            createQuickBuyModal({
                productViewService,
                config,
            });

        const facetRenderer =
            createFacetRenderer({
                facetManager,
                config,
            });

        const advertisementManager =
            new global
                .NostoPlpAdvertisementManager({
                    root,

                    container:
                        SELECTORS.results,

                    debug:
                        config.debug,
                });

        const paginationManager =
            ENABLE_HYBRID_PAGINATION
                ? new global.NostoHybridPaginationManager({
                    container:
                        SELECTORS.pagination,

                    pageParam:
                        LOGICAL_PAGE_PARAM,

                    loadedParam:
                        "loaded",

                    logicalPageSize:
                        PRODUCTS_PER_LOGICAL_PAGE,

                    debug:
                        config.debug,
                })
                : null;

        paginationManager?.hide();

        let controller = null;

        const resultsRenderer =
            createResultsRenderer({
                productViewService,
                advertisementManager,

                getController() {
                    return controller;
                },

                getViewMode() {
                    return currentViewMode;
                },

                config,
            });

        /**
         * Aplica una vista al PLP completo y opcionalmente la persiste.
         */
        function setViewMode(
            viewMode,
            {
                persist = true,
                emit = true,
            } = {}
        ) {
            const nextMode =
                normalizeViewMode(
                    viewMode
                );

            const previousMode =
                currentViewMode;

            currentViewMode =
                nextMode;

            root.dataset.nostoViewMode =
                nextMode;

            advertisementManager
                .sync(
                    nextMode
                );

            const results =
                root.querySelector(
                    SELECTORS.results
                );

            syncEditorialProductCards(
                results,
                nextMode
            );

            global.refreshProductCards?.(
                results
            );

            syncViewModeButtons({
                root,
                viewMode:
                    nextMode,
            });

            if (persist) {
                storeViewMode(
                    nextMode
                );
            }

            if (
                emit &&
                previousMode !==
                nextMode
            ) {
                root.dispatchEvent(
                    new CustomEvent(
                        "nosto:plp:view-mode-change",
                        {
                            bubbles: true,
                            detail: {
                                previousViewMode:
                                    previousMode,

                                viewMode:
                                    nextMode,
                            },
                        }
                    )
                );
            }

            return nextMode;
        }

        controller =
            new (ENABLE_HYBRID_PAGINATION
                ? global.NostoHybridPlpController
                : global.NostoPlpController)({
                categoryId:
                    config.categoryId,

                categoryPath:
                    config.categoryPath,

                client,
                facetManager,
                hitStore,

                renderer:
                    resultsRenderer,

                sortElement:
                    SELECTORS.sort,

                loadMoreElement:
                    SELECTORS.loadMore,

                pageSize:
                    config.pageSize,

                logicalPageSize:
                    PRODUCTS_PER_LOGICAL_PAGE,

                logicalPageParam:
                    LOGICAL_PAGE_PARAM,

                onHybridPaginationChange(state) {
                    paginationManager?.render(
                        state
                    );
                },

                productFields: [
                    "name",
                    "url",
                    "imageUrl",
                ],

                track:
                    "category",

                redirect: true,

                syncPaginationToUrl:
                    true,

                restoreLoadedProducts:
                    true,

                restoreScroll:
                    true,

                debug:
                    config.debug,

                /**
 * Renderiza los facets recibidos y confirma el estado aplicado.
 */
                onFacets(facets) {
                    facetRenderer.render(
                        facets,
                        {
                            markApplied: true,
                        }
                    );
                },

                /**
 * Refleja el estado de carga en la sección completa.
 */
                onLoadingChange(
                    isLoading,
                    activeController
                ) {
                    const showPageOverlay =
                        isLoading &&
                        activeController?.loadingMode !==
                        "append";

                    root.classList.toggle(
                        "is-loading",
                        showPageOverlay
                    );

                    root.dataset.nostoLoading =
                        String(isLoading);

                    root.dataset.nostoLoadingMode =
                        activeController?.loadingMode ||
                        "replace";

                    root.setAttribute(
                        "aria-busy",
                        String(isLoading)
                    );
                },

                /**
                 * Emite un evento cuando el PLP termina de recibir una respuesta.
                 */
                onResponse(response, detail) {
                    root.classList.remove(
                        "has-error"
                    );

                    const results =
                        root.querySelector(
                            SELECTORS.results
                        );

                    syncEditorialProductCards(
                        results,
                        currentViewMode
                    );


                    root.dataset.nostoError =
                        "false";
                    root.dispatchEvent(
                        new CustomEvent(
                            "nosto:plp:response",
                            {
                                bubbles: true,

                                detail: {
                                    response,
                                    ...detail,
                                },
                            }
                        )
                    );
                },

                /**
                 * Emite un evento cuando ocurre un error en el PLP.
                 */
                onError(error) {
                    root.classList.add(
                        "has-error"
                    );

                    root.dataset.nostoError =
                        "true";
                    root.dispatchEvent(
                        new CustomEvent(
                            "nosto:plp:error",
                            {
                                bubbles: true,

                                detail: {
                                    error,
                                    controller,
                                },
                            }
                        )
                    );
                },
            });

        global.NOSTO_PLP = {
            client,
            facetManager,
            hitStore,
            facetRenderer,
            resultsRenderer,
            productViewService,
            quickBuyModal,
            advertisementManager,
            paginationManager,
            controller,

            getViewMode() {
                return currentViewMode;
            },

            setViewMode(viewMode) {
                return setViewMode(
                    viewMode,
                    {
                        persist: true,
                        emit: true,
                    }
                );
            },
        };


        await controller.init();

        bindViewModeControls({
            root,

            getViewMode() {
                return currentViewMode;
            },

            setViewMode,
        });

        bindFiltersDrawerClose();

        root.dataset.nostoInitialized =
            "true";

        delete root.dataset
            .nostoInitializing;

        return global.NOSTO_PLP;
    }

    /**
 * Ejecuta la inicialización cuando el DOM está disponible.
 */
    function boot() {
        init().catch((error) => {
            const root =
                document.querySelector(
                    SELECTORS.root
                );

            if (root) {
                delete root.dataset
                    .nostoInitializing;
            }

            console.error(
                "[Nosto-Plp] No fue posible inicializar el PLP.",
                error
            );
        });
    }

    if (
        document.readyState ===
        "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            boot,
            {
                once: true,
            }
        );
    } else {
        boot();
    }
})(window);